/* pantallas-alumno (B): EL BOTON DE LA CASA, TRABAJANDO Y APRETADO (P1 de la parte 1).

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   - Variantes del Panel: principal (#F27A2E con texto #160A02), secundario (#1B1B1B, borde #2C2C2C),
     rojo (#DC2626, el de borrar en una ventana) y secundario con texto rojo ("Delete this part").
     14 px seminegrita, radio 12. Apagado al 45 %.
   - Altos: 44 en el celular y 40 desde 768 (los botones del Panel); 48 y 40 los de una ventana;
     48 fijo; 52 el de la barra fija del celular; 40 fijo el del recuadro compacto.
   - TRABAJANDO: el texto deja de verse pero sigue ocupando su lugar y el giro va encima, en una
     capa absoluta centrada: el boton no cambia de ancho. Mientras trabaja no se puede tocar.
   - APRETADO: mientras se presiona, 10 % mas oscuro y al 98 % de su tamano. Con "reducir
     movimiento" solo se oscurece: la escala va detras de la variante de movimiento seguro.
   - APAGADO SUAVE: se ve al 45 %, no hace lo suyo, y al tocarlo llama a `alTocarApagado` (que saca el
     aviso del caso). Un `disabled` de verdad no avisaria nada.
   - SIN RED (`sinRed`, seccion 4.1 del encargo): el mismo apagado suave, pero al 38 % ("You need a
     connection"). Si vienen los dos, manda el de la red.

   Las cadenas de clases van COMPLETAS y literales: Tailwind las encuentra escaneando el archivo. */
import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { OPACIDAD_APAGADO, OPACIDAD_SIN_RED } from './estilos';

// Mas oscuro siempre; la escala solo sin "reducir movimiento".
export const APRETADO = 'active:brightness-90 motion-safe:active:scale-[0.98] motion-safe:transition-transform motion-safe:duration-75';

export const ALTOS_DEL_BOTON = Object.freeze({
  normal: 'h-11 md:h-10',
  ventana: 'h-12 md:h-10',
  grande: 'h-12',
  barra: 'h-[52px]',
  compacto: 'h-10',
  chico: 'h-9',
});

export const VARIANTES_DEL_BOTON = Object.freeze({
  principal: 'bg-[#F27A2E] text-[#160A02]',
  secundario: 'bg-[#1B1B1B] border border-[#2C2C2C] text-[#F4F4F3]',
  rojo: 'bg-[#DC2626] text-white',
  secundarioRojo: 'bg-[#1B1B1B] border border-[#2C2C2C] text-[#F87171]',
});

const BASE = 'relative inline-flex items-center justify-center px-4 rounded-xl text-[14px] font-semibold whitespace-nowrap select-none disabled:opacity-45 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-white';

export function clasesDeBoton({ variante = 'principal', alto = 'normal', aLoAncho = false, className = '' } = {}) {
  const v = VARIANTES_DEL_BOTON[variante] || VARIANTES_DEL_BOTON.principal;
  const h = ALTOS_DEL_BOTON[alto] || ALTOS_DEL_BOTON.normal;
  return `${BASE} ${h} ${v} ${APRETADO}${aLoAncho ? ' w-full' : ''}${className ? ` ${className}` : ''}`;
}

/* El contenido de un boton que trabaja. Sirve tambien para un boton que no es de esta pieza: quien lo
   use le pone posicion relativa a su boton. */
export function GiroEncima({ trabajando = false, children, claseDelGiro = 'w-4 h-4' }) {
  return (
    <>
      <span data-parte="texto" className={`inline-flex items-center justify-center gap-1.5 min-w-0${trabajando ? ' invisible' : ''}`}>
        {children}
      </span>
      {trabajando ? (
        <span data-parte="giro" aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
          <Loader2 className={`${claseDelGiro} animate-spin`} />
        </span>
      ) : null}
    </>
  );
}

const Boton = forwardRef(function Boton({
  variante = 'principal', alto = 'normal', aLoAncho = false, trabajando = false, apagadoSuave = false,
  sinRed = false, alTocarApagado, icono: Icono, iconoDerecha: IconoDerecha, disabled = false, type = 'button',
  className = '', testid, onClick, style, children, ...resto
}, ref) {
  const suave = apagadoSuave || sinRed;
  const alTocar = (e) => {
    if (suave) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (alTocarApagado) alTocarApagado(e);
      return;
    }
    if (onClick) onClick(e);
  };
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || trabajando}
      aria-busy={trabajando || undefined}
      aria-disabled={suave || undefined}
      data-trabajando={trabajando ? 'true' : undefined}
      className={clasesDeBoton({ variante, alto, aLoAncho, className })}
      style={suave ? { ...style, opacity: sinRed ? OPACIDAD_SIN_RED : OPACIDAD_APAGADO } : style}
      onClick={alTocar}
      data-testid={testid}
      {...resto}
    >
      <GiroEncima trabajando={trabajando}>
        {Icono ? <Icono className="w-4 h-4 shrink-0" aria-hidden="true" /> : null}
        {children}
        {IconoDerecha ? <IconoDerecha className="w-4 h-4 shrink-0" aria-hidden="true" /> : null}
      </GiroEncima>
    </button>
  );
});

export default Boton;
